import * as React from 'react';
import Stardog from 'stardog';

import { Permission } from 'src/constants/permissions';
import {
  CreationType,
  VirtualGraphMappingInfoByProjectVgId,
} from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { permissionsSelector } from 'src/selectors/permissions';
import {
  MatchResourceFn,
  hasAnyResourcePermission,
  hasPermission,
} from 'src/utils/hasPermission';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export interface DesignerPermissionChecks {
  canCreateDatabase: boolean;
  canCreateDataSource: boolean;
  canImportModel: boolean;
  canNavbarPublish: boolean;
  canPublishUniqueKeySets: boolean;
  canPublishVirtualGraphs: boolean;
  canReadDataSource: boolean;
  canUploadTTL: boolean;
}

export interface DesignerPermissionsContextValue {
  permissions: DesignerPermissionChecks;
  verifyPublishPermissions: (
    databaseId: string,
    isCreatingDatabase: boolean,
    dataSourceIdByVgId: Record<string, string>,
    vgMappingInfoForPublish: VirtualGraphMappingInfoByProjectVgId
  ) => PermissionRequirement | null;
  canWriteSpecificDataSource: (dataSourceId: string) => boolean;
}

export type PermissionRequirement = {
  action: Stardog.user.Action;
  resourceType: Stardog.user.ResourceType;
  /**
   * Custom resource matching behavior. Can be:
   * - A string: Always check against this fixed resource (e.g., '*' for createDatabase)
   * - A function: Custom matching logic (e.g., for named-graph where "myDb\graph" matches "myDb")
   */
  matchResource?: string | MatchResourceFn;
};

type PermissionMethods = {
  [K in keyof typeof REQUIREMENTS]: PermissionRequirementMethod;
};

type PermissionRequirementMethod = PermissionRequirement & {
  /** Check permission for a specific resource */
  hasSpecific: (resource: string) => boolean;
  /** Check permission for any resource */
  hasAny: () => boolean;
};

export const REQUIREMENTS = {
  createDatabase: {
    action: 'CREATE',
    resourceType: 'db',
    // The user MUST have '*' in order to create ANY database!
    // (specifying a single `CREATE:db:music` does NOT work!)
    matchResource: '*',
  },
  createDataSource: {
    action: 'CREATE',
    resourceType: 'data-source',
    // The user MUST have '*' in order to create ANY data-source!
    // (specifying a single `CREATE:data-source:data-source://pilothouse_test_drugs` does NOT work!)
    matchResource: '*',
  },
  createStoredQuery: {
    action: 'CREATE',
    resourceType: 'stored-query',
  },
  createVirtualGraph: {
    action: 'CREATE',
    resourceType: 'virtual-graph',
    // The user MUST have '*' in order to create ANY virtual-graph!
    // (specifying a single `CREATE:virtual-graph:music` does NOT work!)
    matchResource: '*',
  },
  deleteDataSource: {
    action: 'DELETE',
    resourceType: 'data-source',
  },
  deleteVirtualGraph: {
    action: 'DELETE',
    resourceType: 'virtual-graph',
  },
  executeDataSource: {
    action: 'EXECUTE',
    resourceType: 'data-source',
  },
  readDatabase: {
    action: 'READ',
    resourceType: 'db',
  },
  readDataSource: {
    action: 'READ',
    resourceType: 'data-source',
  },
  writeDataSource: {
    action: 'WRITE',
    resourceType: 'data-source',
  },
  readModeling: {
    action: 'READ',
    resourceType: 'modeling',
  },
  readVirtualGraph: {
    action: 'READ',
    resourceType: 'virtual-graph',
  },
  writeDatabase: {
    action: 'WRITE',
    resourceType: 'db',
  },
  writeModeling: {
    action: 'WRITE',
    resourceType: 'modeling',
  },
} as const satisfies Record<string, PermissionRequirement>;

const createPermissionMethods = (
  req: PermissionRequirement,
  permissions: Permission[],
  permissionsLoading: boolean,
  supportsDesignerPermissions: boolean
): PermissionRequirementMethod => {
  // Determine if matchResource is a fixed string or a custom function
  const fixedResource: string | null =
    typeof req.matchResource === 'string' ? req.matchResource : null;
  const matcherFn: MatchResourceFn | undefined =
    typeof req.matchResource === 'function' ? req.matchResource : undefined;

  const hasSpecific = (resource: string) => {
    if (permissionsLoading) {
      return false;
    }
    if (!supportsDesignerPermissions) {
      return true;
    }

    // If matchResource is a string, always check against that fixed resource
    const effectiveResource = fixedResource ?? resource;

    return hasPermission(
      permissions,
      req.action,
      req.resourceType,
      effectiveResource,
      matcherFn
    );
  };

  return {
    ...req,
    hasSpecific,
    hasAny: () => {
      if (permissionsLoading) {
        return false;
      }
      if (!supportsDesignerPermissions) {
        return true;
      }

      // If matchResource is a fixed string, check against that resource
      if (fixedResource) {
        return hasSpecific(fixedResource);
      }

      // Otherwise use standard check for any resource
      // (custom matcherFn doesn't apply since we're not checking a specific target)
      return hasAnyResourcePermission(
        permissions,
        req.action,
        req.resourceType
      );
    },
  };
};

export const DesignerPermissionsContext =
  React.createContext<DesignerPermissionsContextValue | null>(null);

export const useDesignerPermissionsContextValue =
  (): DesignerPermissionsContextValue => {
    const connection = useReduxSelector(connectionSelector);
    const userPermissions = useReduxSelector(permissionsSelector);
    const supportsDesignerPermissions = useReduxSelector(
      isStardogFeatureSupportedSelector(
        'isDesignerPermissionsSupported',
        connection
      )
    );
    const permissionsLoading = !userPermissions;

    const methods = React.useMemo(() => {
      return Object.fromEntries(
        Object.entries(REQUIREMENTS).map(([key, req]) => [
          key,
          createPermissionMethods(
            req,
            userPermissions || [],
            permissionsLoading,
            supportsDesignerPermissions
          ),
        ])
      ) as PermissionMethods;
    }, [permissionsLoading, supportsDesignerPermissions, userPermissions]);

    const permissions = React.useMemo((): DesignerPermissionChecks => {
      const canCreateDatabase = methods.createDatabase.hasAny();
      const canCreateDataSource = methods.createDataSource.hasAny();
      const canCreateVirtualGraph = methods.createVirtualGraph.hasAny();
      const canDeleteDataSource = methods.deleteDataSource.hasAny();
      const canDeleteVirtualGraph = methods.deleteVirtualGraph.hasAny();
      const canExecuteDataSource = methods.executeDataSource.hasAny();
      const canReadDatabase = methods.readDatabase.hasAny();
      const canReadDataSource = methods.readDataSource.hasAny();
      const canReadModeling = methods.readModeling.hasAny();
      const canWriteDatabase = methods.writeDatabase.hasAny();
      const canWriteModeling = methods.writeModeling.hasAny();

      const canImportModel = canReadDatabase;

      return {
        canCreateDatabase,
        canCreateDataSource,
        canReadDataSource: canExecuteDataSource,
        canUploadTTL: canImportModel && canWriteDatabase && canWriteModeling,
        canImportModel,
        canNavbarPublish:
          // User has broad publish permissions if they can create databases,
          // or have all of the required publish permissions for existing DBs.
          canCreateDatabase ||
          (canReadDatabase &&
            canWriteDatabase &&
            canReadModeling &&
            canWriteModeling),
        canPublishUniqueKeySets:
          canCreateDataSource &&
          canReadDataSource &&
          canDeleteDataSource &&
          canDeleteVirtualGraph,
        // not including DELETE:virtual-graph because you don't need it when
        // creating a new VG
        canPublishVirtualGraphs: canCreateVirtualGraph && canReadDataSource,
      };
    }, [methods]);

    const verifyPublishPermissions = React.useCallback(
      (
        databaseId: string,
        isCreatingDatabase: boolean,
        dataSourceIdByVgId: Record<string, string>,
        vgMappingInfoForPublish: VirtualGraphMappingInfoByProjectVgId
      ): PermissionRequirement | null => {
        // Helper to check a permission and return requirement if denied
        const checkPermission = (
          method: PermissionRequirementMethod,
          resource: string
        ): PermissionRequirement | null => {
          const hasPermission = method.hasSpecific(resource);
          return hasPermission ? null : method;
        };

        // Check createDatabase permission if creating a new database
        const databaseChecks = isCreatingDatabase
          ? [checkPermission(methods.createDatabase, '*')]
          : // else, check database permissions.
            // The reason we don't check specific permissions for not-yet-created databases
            // is because stardog will automatically grant these new permissions when the database is created,
            // and having the permissions ahead of time is not required.
            [
              checkPermission(methods.readDatabase, databaseId),
              checkPermission(methods.writeDatabase, databaseId),
              checkPermission(methods.readModeling, databaseId),
              checkPermission(methods.writeModeling, databaseId),
            ];

        // Check data source and virtual graph permissions for each VG
        const vgChecks = Object.entries(vgMappingInfoForPublish).flatMap(
          ([virtualGraphId, { creationType, virtualGraphName }]) => {
            const dataSourceId = dataSourceIdByVgId[virtualGraphId];

            return [
              checkPermission(methods.createVirtualGraph, '*'),
              dataSourceId
                ? checkPermission(
                    methods.readDataSource,
                    `data-source://${dataSourceId}`
                  )
                : null,
              creationType !== CreationType.NEW
                ? checkPermission(
                    methods.deleteVirtualGraph,
                    `virtual://${virtualGraphName}`
                  )
                : null,
            ];
          }
        );

        const results = [...databaseChecks, ...vgChecks];

        // Return first denied permission, or null if all allowed
        return results.find((r) => r !== null) ?? null;
      },
      [methods]
    );

    const canWriteSpecificDataSource = React.useCallback(
      (dataSourceId: string) =>
        methods.writeDataSource.hasSpecific(`data-source://${dataSourceId}`),
      [methods]
    );

    return {
      permissions,
      verifyPublishPermissions,
      canWriteSpecificDataSource,
    };
  };

export const useDesignerPermissions =
  (): DesignerPermissionsContextValue['permissions'] => {
    const context = React.useContext(DesignerPermissionsContext);
    if (!context) {
      throw new Error(
        'useDesignerPermissions must be used within a DesignerPermissionsContext.Provider'
      );
    }
    return context.permissions;
  };

export const useVerifyPublishPermissions =
  (): DesignerPermissionsContextValue['verifyPublishPermissions'] => {
    const context = React.useContext(DesignerPermissionsContext);
    if (!context) {
      throw new Error(
        'useVerifyPublishPermissions must be used within a DesignerPermissionsContext.Provider'
      );
    }
    return context.verifyPublishPermissions;
  };

export const useCanWriteSpecificDataSource =
  (): DesignerPermissionsContextValue['canWriteSpecificDataSource'] => {
    const context = React.useContext(DesignerPermissionsContext);
    if (!context) {
      throw new Error(
        'useCanWriteSpecificDataSource must be used within a DesignerPermissionsContext.Provider'
      );
    }
    return context.canWriteSpecificDataSource;
  };
